// Apolo Planning Mode v2 (E4) — pure builders for the continuation-turn TEXT.
//
// When the user approves a proposed plan (via PlanProposalCard), SessionWorkspace
// fires a NEW turn — plan mode OFF — whose task text must carry (a) the plan's
// steps as a numbered list and (b) the user's answers to the clarifying questions,
// so the executing agent has the full, resolved brief. When the user rejects, a
// fresh plan-mode turn re-plans against the one-line feedback.
//
// Kept pure (no React, no store) so the text shape is unit-testable and the
// component stays thin. The plan shape mirrors present_plan / planStateStore:
//   plan = { summary, steps:[{ n, action, tool?, success? }], questions?:[…] }
//   answers = [{ questionId, value }]  (value: string | string[])

// Resolve the displayed question label for an answer. The structured plan carries
// the question text under `questions[].id`; fall back to the raw id so an answer is
// never dropped just because the question metadata is missing/malformed.
function questionLabel(plan, questionId) {
  const qs = Array.isArray(plan?.questions) ? plan.questions : [];
  const match = qs.find((q) => q && String(q.id) === String(questionId));
  const text = match && typeof match.question === 'string' ? match.question.trim() : '';
  return text || String(questionId);
}

// One answer value → a flat, readable string (multi/array values get comma-joined).
function formatValue(value) {
  if (Array.isArray(value)) {
    return value.map((v) => String(v ?? '').trim()).filter(Boolean).join(', ');
  }
  return String(value ?? '').trim();
}

/**
 * Build the task text for the APPROVE continuation turn (plan mode OFF → execute).
 * Embeds the numbered steps and the user's answers. Always returns a non-empty
 * string (a guarding header) even if the plan/answers are empty, so the turn still
 * tells the agent to execute.
 */
export function buildPlanApprovalText(plan, answers) {
  const steps = Array.isArray(plan?.steps) ? plan.steps : [];
  const lines = [];
  steps
    .filter((s) => s && typeof s === 'object')
    .forEach((s, i) => {
      const action = typeof s.action === 'string' ? s.action.trim() : '';
      if (!action) return;
      const n = Number.isFinite(Number(s.n)) ? Number(s.n) : i + 1;
      lines.push(`${n}. ${action}`);
    });

  const answerList = (Array.isArray(answers) ? answers : [])
    .filter((a) => a && a.questionId != null)
    .map((a) => {
      const val = formatValue(a.value);
      if (!val) return null;
      return `- ${questionLabel(plan, a.questionId)}: ${val}`;
    })
    .filter(Boolean);

  const parts = [EXECUTE_APPROVED_PREFIX];
  if (lines.length) parts.push(lines.join('\n'));
  if (answerList.length) parts.push(`Respostas do usuário:\n${answerList.join('\n')}`);
  // RCA lentidão fix A — em prod o update_todo virava PASSO ISOLADO (2 round-trips
  // LLM/task só pra flipar o board). A instrução exige batching na MESMA resposta.
  parts.push(
    'Marque cada passo com update_todo({id,status:"active"}) ao começar e ({id,status:"done"}) ao concluir — sempre JUNTO com as outras tool calls do passo, na MESMA resposta (nunca numa resposta separada só pra atualizar o board).',
  );
  return parts.join('\n\n');
}

// Auditoria 2026-07-03 (#10): alguns modelos (ex.: DeepSeek) se confundem com o
// histórico de PLAN MODE e RECUSAM executar após a aprovação ("estou em plan
// mode, run_command bloqueado") — o plano fica preso em "Executando". O toolset
// JÁ re-habilita as ferramentas no turno executeApproved (readOnly:false); o
// reforço abaixo tira a ambiguidade pro modelo. Neutro pro Claude (que já
// executa), destrava os que hesitam. Compartilhado com a call-site do
// TodoDropdown (SessionWorkspace) para uma mensagem única.
// Curto de propósito (< 140 chars, sem cláusulas múltiplas): um texto longo
// dispara looksLikeComplexTask e o guard executeApproved não cobre todo caminho.
export const EXECUTE_APPROVED_PREFIX =
  'Plano aprovado — você NÃO está mais em plan mode. run_command/write_file/apply_edit estão DISPONÍVEIS: execute de verdade agora.';

/**
 * Build the task text for the REJECT continuation turn (plan mode STAYS ON →
 * re-plan). The agent re-calls present_plan addressing the one-line feedback.
 */
export function buildPlanRejectText(feedback) {
  const fb = typeof feedback === 'string' ? feedback.trim() : '';
  return fb
    ? `Revise o plano endereçando este feedback: ${fb}`
    : 'Revise o plano: o usuário rejeitou a proposta. Reavalie a abordagem e proponha um novo plano.';
}

// Claude Code parity: the plan card's clarifying questions (clickable options +
// "Outro") must ALWAYS appear, even when the model returned a plan without any.
// If the plan has no usable questions, inject ONE sensible default so the user
// always gets the interactive "asking" UI (pick an option, or write in "Outro",
// or just type in the chat to refine). The model is also directed to supply real
// questions — this is the guarantee for when it doesn't.
const DEFAULT_PLAN_QUESTION = {
  id: 'proceed',
  question: 'Como devo seguir com este plano?',
  options: ['Seguir com este plano', 'Ajustar o escopo', 'Mudar a abordagem'],
  allowOther: true,
};

export function ensurePlanQuestions(plan) {
  if (!plan || typeof plan !== 'object') return plan;
  const qs = Array.isArray(plan.questions)
    ? plan.questions.filter((q) => q && typeof q.question === 'string' && q.question.trim())
    : [];
  return qs.length ? { ...plan, questions: qs } : { ...plan, questions: [DEFAULT_PLAN_QUESTION] };
}
